import React, { useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixIfrsform } from "../../../../api";
import { initialValues, validationSchema } from "./IfrsForm.form";
import "../EnvironmentalRiskMatrixForm/EnvironmentalRiskMatrixForm.scss";

const environmentalRiskMatrixIfrsCtrl = new EnvironmentalRiskMatrixIfrsform();

// Hazards físicos y drivers de transición (Taxonomía UE / IPCC / TCFD) —
// sugerencias de referencia, editables por la organización.
const DRIVER_SUGGESTIONS = [
  "Ola de calor",
  "Sequía / estrés hídrico",
  "Inundación (fluvial)",
  "Inundación (pluvial / lluvias intensas)",
  "Inundación costera",
  "Ciclón / huracán / tifón",
  "Tormenta (incl. granizo, viento)",
  "Incendio forestal",
  "Ola de frío / helada",
  "Aumento del nivel del mar",
  "Aumento de temperatura media",
  "Cambio de patrones de precipitación",
  "Acidificación oceánica",
  "Precio del carbono",
  "Cambio regulatorio / política climática",
  "Litigio climático",
  "Disrupción tecnológica (renovables)",
  "Cambio de demanda de mercado",
  "Riesgo reputacional / greenwashing",
];

function toOptions(list) {
  return list.map((v) => ({ key: v, value: v, text: v }));
}

function computeLevel(values) {
  return (Number(values.probability) || 0) * (Number(values.severity) || 0);
}

export function IfrsForm(props) {
  const { onClose, onReload, item, siteSelected } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const submittingRef = useRef(false);

  const natureOptions = [
    "Físico — agudo",
    "Físico — crónico",
    "Transición — política",
    "Transición — legal",
    "Transición — tecnología",
    "Transición — mercado",
    "Transición — reputación",
    "Oportunidad climática",
  ].map((v) => ({ key: v, value: v, text: v }));
  const horizonOptions = ["Corto (<1 año)", "Medio (1–3 años)", "Largo (>3 años)"].map((v) => ({
    key: v, value: v, text: v,
  }));
  const scaleOptions = [1, 2, 3, 4, 5].map((n) => ({ key: n, value: n, text: String(n) }));

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        const payload = {
          ...formValue,
          vulnerablePercent: formValue.vulnerablePercent === "" ? undefined : formValue.vulnerablePercent,
        };
        if (!item) {
          const res = await environmentalRiskMatrixIfrsCtrl.createEnvironmentalRiskMatrixIfrs(accessToken, {
            ...payload,
            site: user?.site || siteSelected,
          });
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        } else {
          const res = await environmentalRiskMatrixIfrsCtrl.updateEnvironmentalRiskMatrixIfrs(accessToken, item._id, payload);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload?.();
          onClose?.();
        }, 800);
      } catch (error) {
        toast.error(error?.msg || error?.message || t("server_connection_error"));
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const isPhysical = (formik.values.nature || "").startsWith("Físico");
  const level = computeLevel(formik.values);

  return (
    <Form className="risk-matrix-form" onSubmit={formik.handleSubmit}>
      <h3 className="risk-matrix-form__title">{item ? t("risk_matrix_edit_item") : t("risk_matrix_new_item")}</h3>
      <Form.Field error={formik.touched.title && !!formik.errors.title}>
        <label>{t("title")}</label>
        <Form.Input
          name="title"
          placeholder={t("risk_matrix_title_placeholder")}
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.title && formik.errors.title && (
          <Label basic color="red" pointing>{formik.errors.title}</Label>
        )}
      </Form.Field>

      <Form.Field>
        <label>{t("description")}</label>
        <Form.TextArea name="description" value={formik.values.description} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.nature && !!formik.errors.nature}>
          <label>{t("risk_matrix_ifrs_nature")}</label>
          <Form.Select
            options={natureOptions}
            placeholder={t("risk_matrix_ifrs_nature_placeholder")}
            value={formik.values.nature}
            onChange={(_, { value }) => formik.setFieldValue("nature", value)}
            onBlur={() => formik.setFieldTouched("nature", true)}
          />
          {formik.touched.nature && formik.errors.nature && (
            <Label basic color="red" pointing>{formik.errors.nature}</Label>
          )}
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_ifrs_driver")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            clearable
            options={toOptions(DRIVER_SUGGESTIONS)}
            value={formik.values.driver}
            placeholder={t("risk_matrix_ifrs_driver_placeholder")}
            onAddItem={(_, { value }) => DRIVER_SUGGESTIONS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("driver", value)}
          />
        </Form.Field>
      </div>

      <div className="risk-matrix-form__row">
        <Form.Field>
          <label>{t("risk_matrix_ifrs_horizon")}</label>
          <Form.Select
            options={horizonOptions}
            placeholder={t("risk_matrix_ifrs_horizon_placeholder")}
            value={formik.values.horizon}
            onChange={(_, { value }) => formik.setFieldValue("horizon", value)}
          />
        </Form.Field>

        {isPhysical && (
          <Form.Field error={formik.touched.vulnerablePercent && !!formik.errors.vulnerablePercent}>
            <label>{t("risk_matrix_ifrs_vulnerable_percent")}</label>
            <Form.Input
              type="number"
              name="vulnerablePercent"
              min={0}
              max={100}
              placeholder={t("risk_matrix_ifrs_vuln_placeholder")}
              value={formik.values.vulnerablePercent}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.vulnerablePercent && formik.errors.vulnerablePercent && (
              <Label basic color="red" pointing>{formik.errors.vulnerablePercent}</Label>
            )}
          </Form.Field>
        )}
      </div>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.probability && !!formik.errors.probability}>
          <label>{t("risk_matrix_probability")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.probability}
            onChange={(_, { value }) => formik.setFieldValue("probability", value)}
            onBlur={() => formik.setFieldTouched("probability", true)}
          />
          {formik.touched.probability && formik.errors.probability && (
            <Label basic color="red" pointing>{formik.errors.probability}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.severity && !!formik.errors.severity}>
          <label>{t("risk_matrix_financial_magnitude")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.severity}
            onChange={(_, { value }) => formik.setFieldValue("severity", value)}
            onBlur={() => formik.setFieldTouched("severity", true)}
          />
          {formik.touched.severity && formik.errors.severity && (
            <Label basic color="red" pointing>{formik.errors.severity}</Label>
          )}
        </Form.Field>
      </div>

      {(formik.values.probability || formik.values.severity) && (
        <div className="risk-matrix-form__preview">
          <span>{t("risk_matrix_level")}: <b>{level}</b></span>
        </div>
      )}

      <Form.Field>
        <label>{t("risk_matrix_controls")}</label>
        <Form.TextArea name="controls" value={formik.values.controls} onChange={formik.handleChange} />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_action")}</label>
        <Form.TextArea name="action" value={formik.values.action} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__actions">
        <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {!item ? t("save") : t("update")}
        </Button>
        <Button type="button" onClick={() => onClose?.()}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
